/**
 * MODULE: _shared/frontend/lib/frontend/api/assistantContextApi.ts
 * PURPOSE: POST /api/v1/assistant/context/resolve — unified brain context for assistant UI.
 * DEPENDENCIES: axios, ./config (API_CONFIG, getAuthHeaders)
 * PUBLIC_API: resolveAssistantContext, AssistantContextResponse
 * SIDE_EFFECTS: HTTP POST to API gateway
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95 / 150 lines
 * RECENT: 2026-05-26 [2.2.1233] — context_harness on resolve for assistant rail transparency.
 * RECENT_PRIOR: 2026-05-26 [2.2.1225] — risk_alerts on context resolve for advisor rail.
 * RECENT_PRIOR: 2026-05-25 [2.2.1224] — reasoning_citations for inline chat source chips.
 */

import axios from 'axios';
import { API_CONFIG, getAuthHeaders } from './config';
import type { BrainContextHarness, BrainUserLearningStatus } from './brainMemoryApi';

export type AssistantFocusEntity = {
  entity_type?: string;
  entity_id?: string;
  display_name?: string;
  score?: number;
  source?: string;
  match_query?: string;
};

export type AssistantInsightSupportingEvent = {
  id: string;
  event_type?: string;
  source?: string;
  title?: string;
  occurred_at?: string;
};

export type AssistantContextInsight = {
  id?: string;
  entity_type?: string;
  entity_id?: string;
  insight_type?: string;
  title?: string;
  body?: string;
  confidence?: number;
  priority?: number;
  source?: string;
  recommended_actions?: Array<{ label?: string; action?: string }>;
  /** Recent activity_events rows that inform this insight (explainability). */
  supporting_events?: AssistantInsightSupportingEvent[];
  /** Promoted memory_items hints when assistant self-learning applies [2.2.1203]. */
  preference_metadata?: {
    using_preference_for?: string;
    preference_label?: string;
    preference_confidence?: number;
    preference_source?: string;
  };
  /** True when insight was read from workspace_insights store. */
  persisted?: boolean;
};

export type AssistantContextAction = {
  label: string;
  action?: string;
  entity_type?: string;
  entity_id?: string;
  source?: string;
  /** When `navigate`, UI opens CRM deep link instead of prefilling chat [2.2.1248]. */
  /** When `advisor_execute`, UI auto-sends prompt to assistant [2.2.1250]. */
  /** When `open_email_template_create`, UI opens golden-path EmailTemplateCreateModal. */
  /** When `open_sales_target_set`, UI opens generative quota planner modal. */
  execution_mode?:
    | 'navigate'
    | 'prompt'
    | 'advisor_execute'
    | 'compose_draft'
    | 'open_email_template_create'
    | 'open_sales_target_set'
    | string;
  /** Sales Hub golden-path template id when source is email_template_snapshot. */
  template_id?: string;
  template_title?: string;
  /** Ranked send count for self-learning welcome chips (email_template_snapshot). */
  template_uses?: number;
  /** NL utterance sent to assistant when label is shortened for UI [email template chips]. */
  prompt_text?: string;
  recipient_email?: string;
  recipient_name?: string;
};

export type AssistantRelatedEntity = {
  entity_type?: string;
  entity_id?: string;
  display_name?: string;
  status?: string;
  relation_type?: string;
  direction?: string;
  weight?: number;
  metadata?: Record<string, unknown>;
};

export type AssistantContextBreadcrumb = {
  interaction_type?: string;
  tool_name?: string;
  summary?: string;
  created_at?: string;
};

export type AssistantLearnedPreference = {
  memory_key?: string;
  label?: string;
  confidence?: number;
  /** Observed assistant interactions before promotion (from learning_candidates). */
  recurrence_count?: number;
};

/** Shell route snapshot for docked assistant grounding [2.2.1345]. */
export type AssistantShellContext = {
  page_path?: string;
  page_title?: string;
  module?: string;
  selected_record_id?: string | null;
  active_filters?: Record<string, unknown> | null;
  /** Dashboard chart/widget the user asked about [2.2.1346]. */
  active_widget_id?: string | null;
  active_widget_title?: string | null;
  /** Generative modal overlay (e.g. quota planner) for dock grounding. */
  active_modal?: string | null;
  active_modal_title?: string | null;
  active_modal_context?: Record<string, unknown> | null;
};

export type AssistantContextResolveRequest = {
  thread_id?: string | null;
  query_type?: string;
  user_message?: string;
  focus_entity_hint?: { kind?: string; query?: string; entity_id?: string } | null;
  /** Live shell route — module, filters, and page path for idle dock grounding [2.2.1345]. */
  shell_context?: AssistantShellContext | null;
};

export type AssistantToolRoute = {
  query_type?: string;
  execution_mode?: 'immediate' | 'read_only' | 'confirm_first' | string;
  defer_tools?: string[];
  priority_actions?: string[];
  focus_entity?: {
    entity_type?: string;
    entity_id?: string;
    display_name?: string;
  } | null;
  retrieval_stores?: string[];
  vector_search?: boolean;
  parallel_limit?: number;
  user_message_hint?: string | null;
};

export type AssistantSessionDeltaChange = {
  type?: string;
  description?: string;
  delta?: number;
  entity_type?: string;
  entity_id?: string;
};

export type AssistantSessionDelta = {
  has_changes?: boolean;
  changes?: AssistantSessionDeltaChange[];
};

export type AssistantRiskAlert = {
  severity?: 'attention' | 'warning' | 'info' | string;
  title?: string;
  body?: string | null;
  entity_type?: string;
  entity_id?: string;
  insight_type?: string;
  source?: 'insight' | 'session_delta' | string;
  /** Tap-to-ask advisor follow-ups [2.2.1244]. */
  suggested_prompts?: string[];
  /** Action-oriented advisor CTAs (draft email, summarize activity) [2.2.1245|1254]. */
  suggested_actions?: Array<string | AssistantContextAction>;
  /** One-click CRM navigation for entity-linked alerts [2.2.1249]. */
  navigate_action?: AssistantContextAction | null;
};

export type AssistantReasoningCitation = {
  id?: string;
  title?: string;
  source?: string;
  insight_title?: string;
  event_type?: string;
  occurred_at?: string;
};

export type AssistantPromotionPipeline = {
  pending_candidates?: number;
  eligible_candidates?: number;
  almost_ready_candidates?: number;
  active_memories?: number;
  /** Human-readable calibration hint from brain promotion service [2.2.1227]. */
  learning_guidance?: string;
};

/** Rep contact rhythm from world_model.rep_state for advisor timing UX [2.2.1246]. */
export type AssistantRepContactPatterns = {
  calls_per_week_avg?: number;
  emails_per_week_avg?: number;
  preferred_contact_time?: string;
  active_deals_count?: number;
  win_rate_90d?: number;
  summary?: string;
};

export type AssistantContextResponse = {
  tenant_id?: string;
  thread_id?: string | null;
  query_type?: string;
  /** Langflow per-topic slug when resolve was called with a routed topic (UI label). */
  topic_slug?: string | null;
  focus_entity?: AssistantFocusEntity | null;
  related_entities?: AssistantRelatedEntity[];
  top_insights?: AssistantContextInsight[];
  suggested_next_actions?: AssistantContextAction[];
  breadcrumbs?: AssistantContextBreadcrumb[];
  breadcrumbs_markdown?: string;
  scoped_prompt?: string;
  retrieval_plan?: Record<string, unknown>;
  tool_route?: AssistantToolRoute;
  /** Top signals briefing for new sessions [2.2.1215]. */
  proactive_briefing?: string;
  /** Platform metric changes since last session [2.2.1224]. */
  session_delta?: AssistantSessionDelta;
  /** Structured risk and engagement alerts for advisor UX [2.2.1225]. */
  risk_alerts?: AssistantRiskAlert[];
  /** Supporting-event evidence block when user asks why [2.2.1215]. */
  explainability_context?: string;
  /** Flat deduped citations for inline chat source chips [2.2.1224]. */
  reasoning_citations?: AssistantReasoningCitation[];
  /** Promoted self-learning patterns for assistant rail [2.2.1218]. */
  learned_preferences?: AssistantLearnedPreference[];
  /** Self-learning pipeline counts for calibration UX [2.2.1225]. */
  promotion_pipeline?: AssistantPromotionPipeline;
  /** Token/char assembly mode for context rail transparency [2.2.1233]. */
  context_harness?: BrainContextHarness;
  /** User-facing self-learning status (catch-up, almost ready, etc.) [2.2.1244]. */
  learning_status?: BrainUserLearningStatus;
  /** Your calls/emails/week and best contact window from activity ledger [2.2.1246]. */
  rep_contact_patterns?: AssistantRepContactPatterns | null;
};

export async function resolveAssistantContext(
  body: AssistantContextResolveRequest,
  signal?: AbortSignal,
): Promise<AssistantContextResponse> {
  const { data } = await axios.post<AssistantContextResponse>(
    `${API_CONFIG.baseURL}/api/v1/assistant/context/resolve`,
    {
      thread_id: body.thread_id ?? undefined,
      query_type: body.query_type ?? 'general_chat',
      user_message: body.user_message ?? '',
      focus_entity_hint: body.focus_entity_hint ?? undefined,
      shell_context: body.shell_context ?? undefined,
    },
    { headers: getAuthHeaders(), timeout: API_CONFIG.timeout, signal },
  );
  return data ?? {};
}
